import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { relativeWindow } from './pitchWindow';
import {
  TRACE_CURSOR_AT,
  TRACE_VISIBLE_SEC,
  axisRows,
  guideBlocks,
  guideRange,
  headPoint,
  stripLayout,
  stripShiftPct,
  tracePaths,
} from './traceGeometry';
import { TrackCursorLine } from './TrackCursorLine';
import { noteLabel } from '../../../core';

import type { PitchGraphPoint } from '../../../components';
import type { SungRange } from '../capture';
import type { MelodyNote } from '../melody';

/**
 * PitchTraceGraph — **노래 진단**(`RecordViz: 'trace'`)의 시각화. 시간에 따른 음정
 * 곡선을 그린다.
 *
 * 이 진단이 재는 것은 얼마나 오래냐가 아니라 **정답을 따라갔나**다. 그래서 플레이헤드
 * 뒤 3초의 궤적이 화면에 남는다(꼬리는 왼쪽 끝으로 갈수록 흐려진다 — `CURVE_FADE`, 시안
 * `1017:17872`) — 예전엔 여기도 막대 하나가 `발성시간 / 제한시간`으로 자라서,
 * 오래 버틴 사람이 잘한 것처럼 보였다.
 *
 * **축은 흐르고 플레이헤드는 서 있다.** 곡 전체를 정적 좌표로 깐 띠 하나를
 * `translateX`로 밀어서, "지금"이 그리는 영역 가로 30%(`TRACE_CURSOR_AT`)의 세로선에
 * 온다 — 뒤로 3초, 앞으로 7초가 보인다. 이 축은 한 번 되돌린 적이 있다: 시안
 * (`987:5516` → `1017:17872`)의 흐르는 축을 c28f336 이 넣었고, 실기기에서 "전곡이 보여야
 * 다음 음을 미리 읽기 좋다"는 판정으로 01c954a 가 정적 축으로 되돌렸다. 다시 흐르게 한
 * 것은 보컬업식 화면을 목표로 한 결정(2026-09-17)이고, 미리 읽기는 커서를 앞쪽 30% 에 둬서
 * 7초를 보이는 쪽으로 답한다. 판독성은 실기기에서 다시 판정한다 — 조정은 상수 둘이다.
 *
 * 좌표를 프레임마다 다시 계산하지 않는다(c28f336 은 그렇게 해서 47Hz 로 모든 점·블록의
 * x 를 다시 썼다). 프레임마다 바뀌는 것은 띠의 transform 과 곡선 꼬리뿐이다.
 *
 * **왼쪽에 음이름 축을 세운다 — 예전의 "절대 음높이는 안 보여준다" 결정을 뒤집었다.**
 * 그 결정의 근거는 음이름이 판단에 안 쓰인다는 것이었는데, 노래 진단에서는 성립하지
 * 않는다: 정답 블록과 내 곡선이 **어느 음에서** 어긋났는지가 곧 피드백이다. 시안
 * (`1017:17872`)도 왼쪽에 `C3`~`B3`를 세운다. 세로 창은 여전히 `pitchWindow`가 잡는다 —
 * 옥타브 단위로만 자라고 중심이 격자에 붙어서 축이 미끄러지지 않는다.
 *
 * **프롭 구동** — 네이티브 이벤트를 스스로 구독하지 않는다. 구독처는 `useVoiceCapture`
 * 하나뿐이라 mock 브리지(브라우저 개발 경로)도 자동으로 같은 화면을 본다. 앱 공용
 * `RealtimePitchGraph`를 그대로 쓰지 않는 이유가 이것이다 — 그쪽은 스스로 구독하고
 * 음이름·주파수 눈금까지 그려서 두 계약을 동시에 깬다. 순수 계산만 나눠 쓴다.
 */

/**
 * 곡선 꼬리의 페이드. 영역 왼쪽 끝에서 투명, 플레이헤드까지 거리의 2/3 지점부터 불투명 —
 * 막 부른 음은 또렷하고 3초 전의 음은 흐려진다.
 */
const CURVE_FADE = `linear-gradient(to right, transparent 0%, #000 ${(TRACE_CURSOR_AT * 100 * 2) / 3}%)`;

const STRIP_CLASS = 'absolute inset-y-0 left-0 will-change-transform';

/**
 * 플레이헤드가 온전히 보이는 반폭(%). 가로선을 걷어낸 그림이라, 끝까지 찍힌 기둥
 * 하나만으로도 세로 격자선처럼 읽힌다.
 */
const CURSOR_SOLID_HALF_PCT = 28;

interface PitchTraceGraphProps {
  /** 캡처가 누적한 창. 테이크 전체가 들어 있다(livePoints의 창 길이 계약). */
  points: PitchGraphPoint[];
  /** 이번 테이크에서 부른 폭 — 세로 창은 이것만 보고 잡는다. */
  sung: SungRange | null;
  /** 가로축 원점(ms) — 테이크의 첫 프레임 타임스탬프다. 멜로디 t=0도 여기 얹힌다. */
  startMs: number;
  /** 띠가 담는 시간(초) — 테이크 전체. */
  spanSec: number;
  /** 그리는 영역 한 폭이 보이는 시간(초). */
  visibleSec?: number;
  /**
   * 부를 정답 멜로디(노래 진단에만). 회색 블록으로 미리 깔고 그 위에 곡선을 겹친다 —
   * 겹치면 맞은 것이라, 설명 없이 읽힌다.
   */
  guide?: MelodyNote[];
}

export const PitchTraceGraph = ({
  points,
  sung,
  startMs,
  spanSec,
  visibleSec = TRACE_VISIBLE_SEC,
  guide,
}: PitchTraceGraphProps) => {
  const { t } = useTranslation();
  const axis = { startMs, spanMs: spanSec * 1000 };
  // 가이드 음까지 감싸야 아직 안 부른 목표가 화면 밖으로 안 나간다.
  const window = relativeWindow(sung, guide ? guideRange(guide) : null);
  const paths = tracePaths(points, axis, window);
  const head = headPoint(points, axis, window);
  const blocks = guide ? guideBlocks(guide, axis, window) : [];
  // 축은 창이 바뀔 때만 바뀐다 — 피치 프레임마다 도는 렌더에서 라벨 보간을 다시 하지 않는다.
  const { lo, hi } = window;
  const { rows, widest } = useMemo(() => {
    const labelled = axisRows({ lo, hi }).map((row) => ({
      ...row,
      label: noteLabel(t, row.midi),
    }));
    // 축 폭은 고정값이 아니라 가장 긴 라벨이 정한다 — ko는 `솔♯3`, en은 `G#3`이라
    // 로케일마다 글자 수가 다르다.
    return {
      rows: labelled,
      widest: labelled.reduce(
        (a, row) => (row.label.length > a.length ? row.label : a),
        '',
      ),
    };
  }, [lo, hi, t]);
  const visibleMs = visibleSec * 1000;
  // 프레임이 아직 없으면(카운트다운·첫 프레임 전) 축 원점이 지금이다 — 곡 앞 7초가 서 있다.
  const nowMs = points.length ? points[points.length - 1].t : startMs;
  const shift = stripShiftPct(nowMs, axis, visibleMs);
  const stripWidth = stripLayout(axis, visibleMs);
  // 가이드 띠와 곡선 띠가 **같은 값**을 쓴다 — 한쪽만 바뀌면 곡선과 블록이 어긋난다.
  const stripStyle = {
    width: `${stripWidth}%`,
    transform: `translateX(${shift}%)`,
  };

  return (
    <div
      role="img"
      aria-label={t('web.diagnosis.track.traceLabel')}
      // 백분율 높이를 쓰지 않는다 — 셸 루트가 `min-h-dvh`라 조상 높이가 indefinite이고
      // 거기서 `height:100%`는 0이 된다(PitchBarTrack에 같은 주석). 호출자가 flex
      // 컨테이너로 감싸고 여기서는 교차축 stretch로 늘어난다.
      className="relative w-full overflow-hidden"
    >
      {/* 안쪽 한 겹을 위아래로 들여 놓는다. 바깥 상자에 `padding`을 주는 것으로는 안 된다 —
          절대 배치의 `top: %`는 **패딩 박스** 기준이라 `top: 0%`가 여전히 맨 끝이고, 축
          라벨은 각 음에 중심을 맞춰 놓이므로 창 양 끝의 음이름이 절반만 보인다. */}
      {/* 좌우 여백은 **이 층**에 준다. 바깥 상자의 `padding`으로는 안 된다 — 절대 배치의
          컨테이닝 블록은 조상의 **패딩 박스**라 `inset-x-0`이 패딩을 건너뛰고 테두리 끝에
          붙는다(2026-09-21 실기기: 라벨이 화면 왼쪽 끝에 그대로 닿아 있었다). 이 층의
          자식(라벨 열·그리는 상자)은 일반 흐름이라 여기 준 `px-5`가 먹는다. 값은 안내
          카드·버튼과 같다. */}
      <div className="absolute inset-x-0 bottom-3 top-3 flex px-5">
        {/* 음이름 축 — 시안의 `C3`~`B3`. 창이 넓어지면 라벨이 성기게 간다(axisRows).
            보이지 않는 가장 긴 라벨이 열 폭을 잡고, 실제 라벨은 그 위에 절대 배치된다.

            **눈금 점 열은 두지 않는다.** 시안은 라벨 오른쪽에 반음마다 점을 하나
            찍지만, 실기기에서는 그 세로 점 열이 플레이헤드의 점 열과 같은 무게로 읽혀
            어느 쪽이 "지금"인지 갈리지 않았다(2026-09-21). 화면에서 점을 쓰는 곳은
            움직이는 플레이헤드 하나뿐이다.

            라벨 색은 `bk-700`(흰 배경 대비 6.69:1). 시안은 이 자리를 훨씬 연하게 칠하지만
            (픽셀 표본 ≈ 1.5:1) 13px 본문 텍스트라 WCAG AA 4.5:1 을 넘겨야 한다 — 한 계단
            위인 `bk-600` 도 3.56:1 로 모자란다. 가로 격자선을 걷어낸 뒤로는 이 라벨이
            축의 유일한 기준이라 읽히지 않으면 축이 없는 것과 같다. */}
        <div className="relative shrink-0 pr-3 text-[13px] tabular-nums">
          <span
            className="invisible block whitespace-nowrap"
            aria-hidden="true"
          >
            {widest}
          </span>
          {rows.map((row) => (
            <span
              key={row.midi}
              data-testid="trace-axis-note"
              aria-hidden="true"
              className="absolute left-0 -translate-y-1/2 whitespace-nowrap text-[var(--bk-700)]"
              style={{ top: `${row.y}%` }}
            >
              {row.label}
            </span>
          ))}
        </div>

        {/* 그리는 영역을 **자기 상자**로 분리한다. 축 폭을 좌표마다 더하고 빼면(`calc(2.5rem
          + x% * 0.88)`) 곡선·블록·도트가 각자 다른 식을 갖게 되고, 축 폭을 바꿀 때 한
          군데만 고쳐져 서로 어긋난다. 상자를 밀어 두면 안쪽 좌표는 그대로 0..100이다. */}
        <div
          // `overflow-hidden` — 흘러간 가이드 블록·곡선이 축 라벨 위로 넘어오지 않게.
          className="relative min-w-0 flex-1 overflow-hidden"
          aria-hidden="true"
        >
          {/* 흐르는 띠 — 곡 시간 좌표가 여기 안에서만 산다. 플레이헤드·도트는 띠 밖에
              있어서 안 흐른다. `translateX(%)`는 띠 **자기 폭** 기준이다. */}
          <div
            data-testid="trace-strip"
            className={STRIP_CLASS}
            style={stripStyle}
          >
            {/* 가이드는 곡선 **아래**에 깔린다 — 겹쳤을 때 내 목소리가 위로 보여야 한다.
            블록을 SVG 안에 넣지 않는 이유: 세로로 늘어나는 좌표계라 모서리 반경까지 같이
            찌그러진다. 사각형이라 %만으로 놓을 수 있으니 HTML로 얹는다. */}
            {blocks.map((block) => (
              <div
                key={block.id}
                data-testid="pitch-guide-note"
                className="absolute h-3.5 -translate-y-1/2 rounded-full bg-[var(--bk-100)]"
                style={{
                  left: `${block.x}%`,
                  width: `${block.width}%`,
                  top: `${block.y}%`,
                }}
              />
            ))}
          </div>

          {/* 곡선 층 — 꼬리가 영역 왼쪽 끝으로 갈수록 흐려진다(시안 `1017:17872`). 마스크는
              **안 흐르는** 이 층에 건다: 띠에 걸면 띠와 같이 흘러서 페이드 자리가 움직인다.
              그래서 곡선만 같은 폭·같은 transform 의 두 번째 띠에 두고, 가이드 블록은 위의
              띠에 남아 흐려지지 않는다. */}
          <div
            data-testid="trace-curve-layer"
            className="absolute inset-0"
            style={{ maskImage: CURVE_FADE, WebkitMaskImage: CURVE_FADE }}
          >
            <div className={STRIP_CLASS} style={stripStyle}>
              {/* 좌표는 0..100 view 단위다(traceGeometry). 컨테이너를 재지 않으므로 곡선 계산이
                레이아웃에 안 묶이고, 늘어나며 생기는 선 굵기 왜곡은 non-scaling-stroke가 막는다. */}
              <svg
                // **`h-full w-full`은 `inset-0`과 중복이 아니라 필수다.** `<svg>`는 replaced
                // element라 viewBox가 종횡비(1:1)를 주는데, 그 경우 절대 배치의 `bottom` 오프셋이
                // 무시되고 높이가 너비를 따라간다 — 390×544 트랙에서 곡선만 위쪽 390px에 그려지고,
                // %로 배치되는 도트·기준선과 좌표계가 어긋난다(실측).
                className="absolute inset-0 h-full w-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {/* **키는 `d`가 아니라 순번이다.** 마지막 구간의 `d`는 프레임마다 뒤로
                  자라므로 그걸 키로 쓰면 React가 그 `<path>`를 매번 버리고 다시 만든다. 구간은
                  시간 순으로 뒤에만 붙으므로 순번이 안정적인 이름이다(같은 좌표의 한 프레임짜리
                  구간 둘이 키를 나눠 갖는 일도 같이 사라진다). */}
                {paths.map((d, i) => (
                  <path
                    key={i}
                    data-testid="pitch-trace"
                    d={d}
                    fill="none"
                    stroke="hsl(var(--primary))"
                    strokeWidth={4}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
              </svg>
            </div>
          </div>

          {/* 위아래 끝은 페이드시킨다. 끝까지 찍힌 점 기둥은 가로선을 걷어낸 자리에
              세로선을 다시 세운 꼴이 된다. 가운데만 남으면 "지금"은 그대로 읽히고
              격자는 사라진다. */}
          <TrackCursorLine
            at={TRACE_CURSOR_AT}
            testId="trace-cursor"
            solidHalfPct={CURSOR_SOLID_HALF_PCT}
          />

          {head != null && (
            <span
              data-testid="pitch-trace-head"
              // SVG 안에 원을 그리면 축이 따로 늘어나 타원이 된다 — 도트만 HTML로 얹는다.
              // 띠 밖에 있으므로 곡선 끝의 띠 좌표를 영역 좌표로 옮겨 놓는다. `freshHead` 는
              // 최신 프레임보다 조금(≤200ms) 이전 점일 수 있어서, 커서에 박아 두면 선 끝과
              // 떨어진다.
              className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary ring-4 ring-primary/20"
              style={{
                left: `${((head.x + shift) * stripWidth) / 100}%`,
                top: `${head.y}%`,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
